/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useLocation } from 'react-router';

const HASH_OBSERVER_TIMEOUT_MS = 3000;

/**
 * Scrolls to the element matching `location.hash` after navigations.
 * Tries immediately; if the target is not mounted yet (async content),
 * observes the DOM until it appears or a timeout elapses.
 */
export function ScrollToHash() {
    const location = useLocation();

    React.useEffect(() => {
        if (!location.hash) {
            return;
        }

        let id: string;
        try {
            id = decodeURIComponent(location.hash.slice(1));
        } catch {
            return;
        }

        if (!id) {
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let rafId: number | undefined;

        const scrollToElement = (el: Element): void => {
            if (cancelled) {
                return;
            }
            const reduceMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches;
            el.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
                block: 'start',
            });
        };

        const scrollIfFound = (): boolean => {
            const el = document.getElementById(id);
            if (!el) {
                return false;
            }
            // Wait one frame so layout can settle after the element mounts.
            rafId = window.requestAnimationFrame(() => {
                scrollToElement(el);
            });
            return true;
        };

        if (scrollIfFound()) {
            return () => {
                cancelled = true;
                if (rafId !== undefined) {
                    window.cancelAnimationFrame(rafId);
                }
            };
        }

        const observer = new MutationObserver(() => {
            if (scrollIfFound()) {
                observer.disconnect();
                if (timer !== undefined) {
                    clearTimeout(timer);
                }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        timer = setTimeout(() => {
            observer.disconnect();
        }, HASH_OBSERVER_TIMEOUT_MS);

        return () => {
            cancelled = true;
            observer.disconnect();
            if (timer !== undefined) {
                clearTimeout(timer);
            }
            if (rafId !== undefined) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [location.pathname, location.hash]);

    return null;
}
